import { normalizeThresholds } from '../utils/bands';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  doc,
  documentId,
  getDocs,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import {
  getOrganization,
  getOrganizationsForUser,
  subscribeToOrganization,
  subscribeToUserOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  addSite,
  updateSite,
  deleteSite,
  addDepartment,
  updateDepartment,
  deleteDepartment,
  addTeam,
  updateTeam,
  deleteTeam,
  updateThresholds,
  updateLibraryPrefs,
} from '../../../services/organizationService';

/**
 * Hook to fetch and manage a single organization by ID with real-time updates.
 * Returns org data, derived structure (sites, departments, teams), thresholds,
 * and an updateSettings function for modifying org configuration.
 */
export function useOrganization(organizationId) {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setOrganization(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToOrganization(organizationId, (org) => {
      setOrganization(org);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [organizationId]);

  // Derived convenience properties from organization data
  const sites = organization?.sites || [];
  const departments = organization?.departments || [];
  const teams = organization?.teams || [];
  const thresholds = normalizeThresholds(organization?.thresholds); // canonical {reliability,…} (P4-21)

  /**
   * Update organization settings (name, industry, thresholds, etc.)
   */
  const updateSettings = useCallback(
    async (updates) => {
      if (!organizationId) throw new Error('No organization selected');
      return updateOrganization(organizationId, updates);
    },
    [organizationId]
  );

  return {
    organization,
    sites,
    departments,
    teams,
    thresholds,
    loading,
    error,
    updateSettings,
  };
}

/**
 * Hook to fetch all organizations for the current user with real-time updates.
 */
// ─── Selected-organization store ────────────────────────────────────────────
// Pages historically take `organizations[0]` as "the" org. Instead of touching
// every page, useUserOrganizations sorts the user's SELECTED org to index 0.
// Selection persists per user and notifies all mounted hooks (module-level bus).
const selectedOrgListeners = new Set();
const selectedOrgStorageKey = (userId) => `repsit_selected_org_${userId}`;

function getStoredSelectedOrgId(userId) {
  try {
    return window.localStorage.getItem(selectedOrgStorageKey(userId));
  } catch {
    return null;
  }
}

/** Switch the active organization for every D&A page (persists per user). */
export function setSelectedOrganization(userId, orgId) {
  try {
    window.localStorage.setItem(selectedOrgStorageKey(userId), orgId);
  } catch {
    /* storage unavailable — selection still applies for this session */
  }
  selectedOrgListeners.forEach((fn) => fn(orgId));
}

export function useUserOrganizations(userId) {
  // Orgs are visible to a user through EITHER path:
  //  1. their UID is in the org's `admins` array (owners/org admins), OR
  //  2. they hold a role for the org in Hugo_ToolAccess/{uid}.roles
  //     (site_manager, department_lead, team_lead, respondent, ...).
  // Members provisioned via hugoCreateOrgUser only get (2), so resolving
  // through `admins` alone left them with an empty org list (blank app).
  const [adminOrgs, setAdminOrgs] = useState([]);
  const [roleOrgs, setRoleOrgs] = useState([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(() => getStoredSelectedOrgId(userId));

  // Stay in sync with selection changes made anywhere in the app.
  useEffect(() => {
    setSelectedOrgId(getStoredSelectedOrgId(userId));
    const listener = (orgId) => setSelectedOrgId(orgId);
    selectedOrgListeners.add(listener);
    return () => selectedOrgListeners.delete(listener);
  }, [userId]);

  // Path 1: orgs where the user is in the admins array (live).
  useEffect(() => {
    if (!userId) {
      setAdminOrgs([]);
      setAdminLoading(false);
      return;
    }
    setAdminLoading(true);
    const q = query(
      collection(db, 'Hugo_Organizations'),
      where('admins', 'array-contains', userId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setAdminOrgs(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAdminLoading(false);
      },
      (err) => {
        console.error('Error subscribing to user organizations:', err);
        setError(err.message);
        setAdminOrgs([]);
        setAdminLoading(false);
      }
    );
    return () => unsubscribe();
  }, [userId]);

  // Path 2: orgs the user holds a role in via Hugo_ToolAccess (live on the
  // access doc; org docs fetched by id whenever the role set changes).
  useEffect(() => {
    if (!userId) {
      setRoleOrgs([]);
      setRolesLoading(false);
      return;
    }
    setRolesLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'Hugo_ToolAccess', userId),
      async (accessSnap) => {
        try {
          const roles = accessSnap.exists() ? accessSnap.data().roles || {} : {};
          const orgIds = Object.keys(roles);
          if (orgIds.length === 0) {
            setRoleOrgs([]);
            setRolesLoading(false);
            return;
          }
          // documentId() `in` queries cap at 10 ids — chunk.
          const chunks = [];
          for (let i = 0; i < orgIds.length; i += 10) chunks.push(orgIds.slice(i, i + 10));
          const results = await Promise.all(
            chunks.map((ids) =>
              getDocs(query(collection(db, 'Hugo_Organizations'), where(documentId(), 'in', ids)))
            )
          );
          setRoleOrgs(results.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
          setRolesLoading(false);
        } catch (err) {
          console.error('Error resolving role-based organizations:', err);
          setError(err.message);
          setRoleOrgs([]);
          setRolesLoading(false);
        }
      },
      (err) => {
        console.error('Error subscribing to tool access:', err);
        setError(err.message);
        setRoleOrgs([]);
        setRolesLoading(false);
      }
    );
    return () => unsubscribe();
  }, [userId]);

  // Merge, de-duplicated by id (admin path wins on conflicts).
  //
  // MEMOIZED — this used to run in the render body, producing a NEW array
  // identity on every render. Anything downstream that keyed an effect or
  // memo on `organizations` (e.g. useNotificationFeed's orgIds) re-fired on
  // every render, tearing down and recreating Firestore listeners in a loop —
  // each resubscribe re-billing its full initial snapshot. One open tab
  // burned ~400 document reads/second (~17M reads/day project-wide).
  const organizations = useMemo(() => {
    const seen = new Set(adminOrgs.map((o) => o.id));
    let orgs = [...adminOrgs, ...roleOrgs.filter((o) => !seen.has(o.id))];

    // Selected org (if still accessible) sorts to index 0 — pages treat
    // organizations[0] as the active org.
    if (selectedOrgId) {
      const idx = orgs.findIndex((o) => o.id === selectedOrgId);
      if (idx > 0) {
        orgs = [orgs[idx], ...orgs.slice(0, idx), ...orgs.slice(idx + 1)];
      }
    }
    return orgs;
  }, [adminOrgs, roleOrgs, selectedOrgId]);

  const loading = adminLoading || rolesLoading;

  return { organizations, loading, error, selectedOrgId: organizations[0]?.id || null };
}

/**
 * Hook providing organization management operations.
 */
export function useOrganizationActions() {
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);

  const withSaving = useCallback(async (fn) => {
    setSaving(true);
    setActionError(null);
    try {
      const result = await fn();
      return result;
    } catch (err) {
      setActionError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    saving,
    actionError,
    clearError: () => setActionError(null),
    createOrganization: (data) => withSaving(() => createOrganization(data)),
    updateOrganization: (orgId, updates) => withSaving(() => updateOrganization(orgId, updates)),
    deleteOrganization: (orgId) => withSaving(() => deleteOrganization(orgId)),
    addSite: (orgId, data) => withSaving(() => addSite(orgId, data)),
    updateSite: (orgId, siteId, data) => withSaving(() => updateSite(orgId, siteId, data)),
    deleteSite: (orgId, siteId) => withSaving(() => deleteSite(orgId, siteId)),
    addDepartment: (orgId, data) => withSaving(() => addDepartment(orgId, data)),
    updateDepartment: (orgId, deptId, data) => withSaving(() => updateDepartment(orgId, deptId, data)),
    deleteDepartment: (orgId, deptId) => withSaving(() => deleteDepartment(orgId, deptId)),
    addTeam: (orgId, data) => withSaving(() => addTeam(orgId, data)),
    updateTeam: (orgId, teamId, data) => withSaving(() => updateTeam(orgId, teamId, data)),
    deleteTeam: (orgId, teamId) => withSaving(() => deleteTeam(orgId, teamId)),
    updateThresholds: (orgId, thresholds) => withSaving(() => updateThresholds(orgId, thresholds)),
    updateLibraryPrefs: (orgId, library) => withSaving(() => updateLibraryPrefs(orgId, library)),
  };
}

export default useOrganization;
